/**
 * Les filtres de la liste d'attente (Planification › Vacances › Liste d'attente).
 *
 * L'écran chargeait toute la table — 1 129 lignes depuis juin 2025 au
 * 17/10/2026, converties et annulées comprises — et les rangeait par session
 * dans l'ordre de leur première inscription : les 8 sessions de la semaine à
 * venir arrivaient après 138 sessions passées, et une ligne convertie ou
 * annulée s'affichait « En attente ». Il s'ouvre désormais sur la semaine,
 * sessions archivées exclues, inscriptions actives seules.
 *
 * Tout ce qui décide d'une ligne vit ici, sans React ni Supabase. Les dates
 * de session sont des chaînes `AAAA-MM-JJ` comparées comme telles : l'ordre
 * lexicographique est l'ordre chronologique.
 */

export type Quand = 'semaine' | 'a-venir' | 'passees' | 'toutes';

export const QUANDS: readonly { cle: Quand; libelle: string }[] = [
  { cle: 'semaine', libelle: 'Cette semaine' },
  { cle: 'a-venir', libelle: 'En cours et à venir' },
  { cle: 'passees', libelle: 'Passées' },
  { cle: 'toutes', libelle: 'Toutes les dates' },
];

/**
 * « Cette semaine » : en cours aujourd'hui, ou qui commence dans les 7 jours.
 * Pas la semaine civile : un samedi, elle est finie, et les sessions qui
 * comptent commencent lundi.
 */
export const HORIZON_SEMAINE_JOURS = 7;

export type StatutAttente = 'waiting' | 'invited' | 'converted' | 'cancelled';
export type FiltreStatut = 'actifs' | StatutAttente | 'tous';

export const FILTRES_STATUT: readonly { cle: FiltreStatut; libelle: string }[] = [
  { cle: 'actifs', libelle: 'En attente et invités' },
  { cle: 'waiting', libelle: 'En attente' },
  { cle: 'invited', libelle: 'Invités' },
  { cle: 'converted', libelle: 'Convertis' },
  { cle: 'cancelled', libelle: 'Annulés' },
  { cle: 'tous', libelle: 'Tous les statuts' },
];

export interface Filtres {
  quand: Quand;
  avecArchivees: boolean;
  centre: string | null;
  /** Le groupe est le stage : « Les Aigles 9 - 13 ans », « Vélo (4/5 ans) »… */
  groupe: string | null;
  statut: FiltreStatut;
  session: string | null;
}

export const FILTRES_PAR_DEFAUT: Filtres = {
  quand: 'semaine',
  avecArchivees: false,
  centre: null,
  groupe: null,
  statut: 'actifs',
  session: null,
};

export interface SessionFiltrable {
  id: string;
  start_date: string;
  end_date: string;
  archived: boolean;
  center_id: string;
  stage_id: string;
  centre_nom: string;
  groupe_nom: string;
}

export interface EntreeFiltrable {
  id: string;
  activity_id: string;
  status: string;
  created_at: string;
}

export function ajouterJours(date: string, jours: number): string {
  const d = new Date(Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10) + jours));
  return d.toISOString().slice(0, 10);
}

export function dansLaPeriode(
  s: Pick<SessionFiltrable, 'start_date' | 'end_date'>,
  quand: Quand,
  aujourdhui: string,
): boolean {
  switch (quand) {
    case 'semaine':
      return s.end_date >= aujourdhui && s.start_date <= ajouterJours(aujourdhui, HORIZON_SEMAINE_JOURS);
    case 'a-venir':
      return s.end_date >= aujourdhui;
    case 'passees':
      return s.end_date < aujourdhui;
    case 'toutes':
      return true;
  }
}

/** Dates et archivage seuls : le périmètre dans lequel on choisit un centre ou un groupe. */
export function dansLePerimetre(s: SessionFiltrable, f: Filtres, aujourdhui: string): boolean {
  return (f.avecArchivees || !s.archived) && dansLaPeriode(s, f.quand, aujourdhui);
}

/**
 * Une session choisie l'emporte sur les autres filtres de session : le lien
 * « Voir la liste d'attente complète » de la programmation doit ouvrir sa
 * session, même archivée ou passée. L'écran efface ce choix dès qu'un autre
 * filtre change.
 */
export function sessionRetenue(s: SessionFiltrable, f: Filtres, aujourdhui: string): boolean {
  if (f.session) return s.id === f.session;
  return (
    dansLePerimetre(s, f, aujourdhui) &&
    (!f.centre || s.center_id === f.centre) &&
    (!f.groupe || s.stage_id === f.groupe)
  );
}

export function estActive(statut: string): boolean {
  return statut === 'waiting' || statut === 'invited';
}

export function statutRetenu(statut: string, filtre: FiltreStatut): boolean {
  if (filtre === 'tous') return true;
  if (filtre === 'actifs') return estActive(statut);
  return statut === filtre;
}

/**
 * Le rang de chaque inscription active dans sa session, par date d'ajout.
 * Une ligne convertie ou annulée n'a pas de rang : elle n'est plus dans la
 * file, et la compter décalait tous les numéros qui la suivaient.
 */
export function rangsDansLaFile(entrees: readonly EntreeFiltrable[]): Map<string, number> {
  const parSession = new Map<string, EntreeFiltrable[]>();
  for (const e of entrees) {
    if (!estActive(e.status)) continue;
    const file = parSession.get(e.activity_id) ?? [];
    file.push(e);
    parSession.set(e.activity_id, file);
  }
  const rangs = new Map<string, number>();
  for (const file of parSession.values()) {
    file
      .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at) || a.id.localeCompare(b.id))
      .forEach((e, i) => rangs.set(e.id, i + 1));
  }
  return rangs;
}

/** En cours et à venir d'abord, la plus proche en tête ; puis les passées, la plus récente en tête. */
export function comparerSessions(a: SessionFiltrable, b: SessionFiltrable, aujourdhui: string): number {
  const passeeA = a.end_date < aujourdhui;
  const passeeB = b.end_date < aujourdhui;
  if (passeeA !== passeeB) return passeeA ? 1 : -1;
  const parDebut = passeeA ? b.start_date.localeCompare(a.start_date) : a.start_date.localeCompare(b.start_date);
  return (
    parDebut ||
    a.centre_nom.localeCompare(b.centre_nom, 'fr') ||
    a.groupe_nom.localeCompare(b.groupe_nom, 'fr')
  );
}

export interface Option {
  valeur: string;
  libelle: string;
}

/**
 * Les centres ou les groupes des sessions que `garder` retient, triés par
 * nom. La valeur choisie y reste même hors de ce périmètre : une liste
 * déroulante qui ne la porte plus afficherait un autre choix que celui qui
 * filtre.
 */
export function optionsDe(
  sessions: readonly SessionFiltrable[],
  cle: 'centre' | 'groupe',
  garder: (s: SessionFiltrable) => boolean,
  retenue: string | null,
): Option[] {
  const options = new Map<string, string>();
  for (const s of sessions) {
    const valeur = cle === 'centre' ? s.center_id : s.stage_id;
    if (garder(s) || valeur === retenue) {
      options.set(valeur, cle === 'centre' ? s.centre_nom : s.groupe_nom);
    }
  }
  return [...options]
    .map(([valeur, libelle]) => ({ valeur, libelle }))
    .sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr'));
}

/*
  Les filtres vivent dans l'adresse : un lien partagé ou rouvert montre la
  même liste. `activity` garde le nom que lui donne déjà le lien de la
  programmation (`WaitingListModal`).
*/
export function lireFiltres(params: URLSearchParams): Filtres {
  const quand = params.get('quand');
  const statut = params.get('statut');
  return {
    quand: QUANDS.find((q) => q.cle === quand)?.cle ?? FILTRES_PAR_DEFAUT.quand,
    avecArchivees: params.get('archivees') === '1',
    centre: params.get('centre') || null,
    groupe: params.get('groupe') || null,
    statut: FILTRES_STATUT.find((s) => s.cle === statut)?.cle ?? FILTRES_PAR_DEFAUT.statut,
    session: params.get('activity') || null,
  };
}

export function ecrireFiltres(f: Filtres): URLSearchParams {
  const params = new URLSearchParams();
  if (f.quand !== FILTRES_PAR_DEFAUT.quand) params.set('quand', f.quand);
  if (f.avecArchivees) params.set('archivees', '1');
  if (f.centre) params.set('centre', f.centre);
  if (f.groupe) params.set('groupe', f.groupe);
  if (f.statut !== FILTRES_PAR_DEFAUT.statut) params.set('statut', f.statut);
  if (f.session) params.set('activity', f.session);
  return params;
}
